import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { siteNames, createUrlResolver, getLanguageFromLocale } from "@seek/melways-sites";
import { useTranslations } from "@vocab/react";
import { Box, PageBlock, Text, IconCritical, Strong, Hidden, TextLink, TextLinkButton } from "braid-design-system";
import { useEmployerHeaderContext, defaultPermissionOptions, getAuthenticationStatus, EmployerHeaderProvider, DefaultLinkRenderer, isAuthenticated } from "../EmployerFooter/EmployerFooter.mjs";
import { translations as translations$1 } from "../../EmployerHeader/.vocab/index.mjs";
import { BrandedLogo } from "./components/BrandedLogo/BrandedLogo.mjs";
import { createContext, useState, useEffect, useContext } from "react";
import { AuthN, getConfig } from "@seek/online-identity";
import * as pageUrl from "@seek/adv-constants";
import { UNIFIED_FINANCE_PHONE_NUMBER } from "@seek/adv-constants";
import { EMPLOYER_HELP_CENTRE_URL } from "@seek/shared-constants";
import { translations } from "../../EmployerHeader/components/CreditHoldAlert/.vocab/index.mjs";
import { HeaderActions } from "./components/HeaderActions/HeaderActions.mjs";
import * as Apollo from "@apollo/client";
import { gql } from "@apollo/client";
import { MobileMenuDropdown } from "./components/MobileMenuDropdown/MobileMenuDropdown.mjs";
import { DesktopNavigation } from "./components/Navigation/DesktopNavigation.mjs";
import { DesktopSubNavigation } from "./components/Navigation/DesktopSubNavigation.mjs";
import { ScreenReaderSkipLink } from "./components/ScreenReaderHelpers/ScreenReaderSkipLink.mjs";
import { ScreenReaderSkipTarget } from "./components/ScreenReaderHelpers/ScreenReaderSkipTarget.mjs";
import { analyticsEvents, analyticsDesktopHeaderEvents, filterLinksOnSiteAndHref } from "../EmployerFooter/AuthenticatedFooterLinks/AuthenticatedFooterLinks.mjs";
import { root, divider, logoContent, actionsContent } from "./EmployerHeader.css.mjs";
const testTags = {
  // Header menu items
  HOME: "home-page",
  JOBS: "jobs-page",
  PRODUCTS: "products-page",
  HIRING_ADVICE: "hiring-advice",
  MARKET_INSIGHTS: "market-insights",
  ANALYTICS: "analytics",
  // Jobs sub menu items
  JOBS_OPEN: "jobs-open-page",
  JOBS_EXPIRED: "jobs-expired-page",
  JOBS_DRAFT: "jobs-draft-page",
  JOBS_MANAGE_CANDIDATES: "jobs-manage-candidates-page",
  // Products sub menu items
  PRODUCTS_HUB: "products-hub",
  PRODUCTS_JOB_ADS: "job-ads-page",
  PRODUCTS_DISCOUNTS: "discounts-page",
  PRODUCTS_FIND_A_RECRUITER: "find-a-recruiter-page",
  PRODUCTS_CANDIDATE_MANAGEMENT: "candidate-management-page",
  PRODUCTS_TALENT_SEARCH: "talent-search-page",
  PRODUCTS_BRANDING: "branding-page",
  PRODUCTS_COMPANY_REVIEWS: "company-reviews-page",
  PRODUCTS_COMPANY_PROFILES: "company-profiles-page",
  // Hiring Advice sub menu items
  HIRING_ADVICE_AD_WRITING: "ad-writing-page",
  HIRING_ADVICE_TALENT_ACQUISITION: "talent-acquisition-page",
  HIRING_ADVICE_SCREENING_CANDIDATES: "screening-candidates-page",
  HIRING_ADVICE_INTERVIEWS: "interviews-page",
  HIRING_ADVICE_LEGAL: "legal-page",
  HIRING_ADVICE_ATTRACTING_CANDIDATES: "attracting-candidates-page",
  HIRING_ADVICE_HIRING_PROCESS: "hiring-process-page",
  HIRING_ADVICE_ENGAGING_WORKFORCE: "engaging-workforce-page",
  // Market insights sub menu items
  MARKET_INSIGHTS_EMPLOYMENT_TRENDS: "employment-trends-page",
  MARKET_INSIGHTS_EMPLOYMENT_SALARY_TRENDS: "employment-salary-trends-page",
  MARKET_INSIGHTS_LAWS_OF_ATTRACTION: "laws-of-attraction-page",
  MARKET_INSIGHTS_TALENT_TALKS: "talent-talks-page",
  MARKET_INSIGHTS_FUTURE_OF_WORK: "future-of-work-page",
  MARKET_INSIGHTS_SALARY_TRENDS: "salary-trends-page",
  MARKET_INSIGHTS_CANDIDATE_INSIGHTS: "candidate-insights-page",
  MARKET_INSIGHTS_TALENT_ATTRACTION_LAB: "talent-attraction-lab-page",
  MARKET_INSIGHTS_SEEK_EMPLOYMENT_TRENDS: "seek-employment-trends-page",
  MARKET_INSIGHTS_WORKFORCE_INSIGHTS: "workforce-insights-page",
  MARKET_INSIGHTS_REPORTS: "reports-page",
  // Analytics sub menu items
  ANALYTICS_AD_USAGE: "ad-usage-page",
  ANALYTICS_TALENT_SEARCH_USAGE: "talent-search-usage-page",
  ANALYTICS_ROLE_INSIGHTS: "role-insights-page",
  // Logged In Account Dropdowns
  SWITCH_ACCOUNT: "switch-account-page",
  ACCOUNT_DETAILS: "account-details-page",
  YOUR_TEAM: "users-page",
  PAYABLE_INVOICES: "payable-invoices-page",
  INVOICE_HISTORY: "invoice-history-page",
  COMPANY_PROFILE: "company-profile-page",
  LOGOS: "logos-page",
  AD_PRICE_LOOKUP: "ad-price-lookup-page",
  CONTACT_US: "contact-us-page",
  SIGN_OUT: "sign-out-page",
  // Sign in and registation
  SIGN_IN: "sign-in-page",
  REGISTER: "register-page",
  CREATE_A_JOB_AD: "create-a-job-ad-button",
  LOGO: "seek-logo"
};
const getMockMessageCount = (path) => {
  if (!path.includes("viewMode=story&id=adv-header-footer-employerheader--chat")) {
    return void 0;
  }
  if (path.includes("with-messages")) {
    return 11;
  }
  return 0;
};
const getAuthN = () => new AuthN(getConfig("seekAnz-hirer", window.location));
const getToken = async () => {
  if (window.location.protocol === "http:") {
    return Promise.resolve(void 0);
  }
  try {
    const token = await getAuthN().getToken();
    return typeof token === "string" ? token : void 0;
  } catch (err) {
    return void 0;
  }
};
const safeGetPath = () => {
  if (typeof window === "undefined" || !window.location) {
    return "/";
  }
  const { pathname, search, hash } = window.location;
  return `${pathname || "/"}${search || ""}${hash || ""}`;
};
const supportedPaths = [
  "/account",
  "/ad-budgets",
  "/adcentre",
  "/analytics",
  "/branding",
  "/candidates",
  "/chat",
  "/contactus",
  "/dashboard",
  "/hiring-advice",
  "/invoicing",
  "/jobs",
  "/market-insights",
  "/products",
  "/your-candidates",
  "viewMode=story&id=adv-header-footer-employerheader--chat"
];
const excludedPaths = ["/checkout", "/account/registration", "/account/select"];
const isSupportedPath = (path) => supportedPaths.some((supported) => path.includes(supported)) && !excludedPaths.some((excluded) => path.includes(excluded));
const defaultValue = {
  chatUser: void 0,
  shouldDisplayChatLink: false
};
const ChatContext = createContext(defaultValue);
const useChatContext = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error("useChatContext must be used within a ChatProvider");
  }
  return context;
};
const formatToken = (token) => /^Bearer .*/.test(token) ? token : `Bearer ${token}`;
const getChatServiceEndpoint = (isProd) => `https://marketplace-chat-api${isProd ? "" : ".staging"}.cloud.seek.com.au`;
const ChatProvider = ({
  children,
  authenticationStatus,
  isAgency
}) => {
  const [visible, setVisible] = useState(false);
  const [data, setData] = useState(void 0);
  const path = safeGetPath();
  useEffect(() => {
    const fetchData = async () => {
      try {
        const isProd = !/\.staging|local|localhost$/.test(window.location.host);
        const token = await getToken();
        if (!token) {
          throw new Error("No token found");
        }
        const response = await fetch(`${getChatServiceEndpoint(isProd)}/user`, {
          method: "GET",
          headers: {
            Authorization: formatToken(token)
          }
        });
        if (response.status === 404) {
          return;
        }
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        const result = await response.json();
        if (typeof result === "object" && "unreadMessageCount" in result) {
          setData(result);
          setVisible(true);
        }
      } catch (ex) {
      } finally {
      }
    };
    const isSignedInToAccount = authenticationStatus === "authenticated";
    const isAllowed = isSignedInToAccount && !isAgency && isSupportedPath(path);
    const mockMessageCount = getMockMessageCount(path);
    const useMockData = mockMessageCount !== void 0;
    if (!useMockData && isAllowed) {
      fetchData();
    } else if (useMockData) {
      setTimeout(() => {
        setData({ unreadMessageCount: mockMessageCount });
        setVisible(true);
      }, 200);
    }
    return () => {
    };
  }, [authenticationStatus, isAgency, path]);
  return /* @__PURE__ */ jsx(
    ChatContext.Provider,
    {
      value: { chatUser: data, shouldDisplayChatLink: visible },
      children
    }
  );
};
const CreditHoldAlert = ({ site }) => {
  const { urlResolver } = useEmployerHeaderContext();
  const { t } = useTranslations(translations);
  const href = urlResolver({ path: EMPLOYER_HELP_CENTRE_URL[site] });
  const financePhoneNumber = UNIFIED_FINANCE_PHONE_NUMBER[site];
  const showFinancePhoneNumber = financePhoneNumber && (site === siteNames.employer.seek.au || site === siteNames.employer.seek.nz);
  return /* @__PURE__ */ jsx(Box, { id: "credit-hold-alert", paddingY: "medium", background: "criticalLight", children: /* @__PURE__ */ jsx(PageBlock, { width: "medium", children: /* @__PURE__ */ jsxs(Text, { tone: "critical", icon: /* @__PURE__ */ jsx(IconCritical, {}), children: [
    /* @__PURE__ */ jsx(Strong, { children: t("Overdue account is on hold") }),
    " ",
    /* @__PURE__ */ jsx(Hidden, { above: "mobile", children: /* @__PURE__ */ jsx("br", {}) }),
    /* @__PURE__ */ jsxs(Box, { component: "span", children: [
      t("Account admins"),
      " ",
      showFinancePhoneNumber ? t("Call finance", { financePhoneNumber }) : t("Visit the Help Centre", {
        TextLink: (children) => /* @__PURE__ */ jsx(TextLink, { href, children })
      })
    ] })
  ] }) }) });
};
const defaultOptions = {};
const CustomerServiceViewUrlDocument = gql`
  query CustomerServiceViewUrl($headerFooterInput: HeaderFooterContextInput) {
    headerFooterContext {
      identityContext {
        act(input: $headerFooterInput) {
          csToggleHref
        }
      }
    }
  }
`;
function useCustomerServiceViewUrlLazyQuery(baseOptions) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useLazyQuery(CustomerServiceViewUrlDocument, options);
}
const actorDisplayText = (email) => {
  let displayText = "Operator";
  if (email && email.indexOf("@") > -1) {
    displayText = email.substring(0, email.indexOf("@"));
  }
  return displayText;
};
const actAsDisplayText = (email, additionalInfo) => /* @__PURE__ */ jsxs(Fragment, { children: [
  email,
  additionalInfo ? /* @__PURE__ */ jsxs(Fragment, { children: [
    " ",
    /* @__PURE__ */ jsxs("span", { "data-hj-masked": "true", "data-dd-privacy": "mask", children: [
      "(",
      additionalInfo,
      ")"
    ] })
  ] }) : null
] });
const ImpersonationBanner = () => {
  const { impersonationContext: context } = useEmployerHeaderContext();
  const [getCustomerServiceViewUrl] = useCustomerServiceViewUrlLazyQuery();
  if (!context) {
    return null;
  }
  const { operatorEmail, isCsView, additionalInfo, subjectEmail } = context;
  const actor = actorDisplayText(operatorEmail);
  const actAs = actAsDisplayText(subjectEmail, additionalInfo);
  const onCsToggleClick = async () => {
    var _a, _b, _c;
    const location = {
      pathname: "/",
      search: ""
    };
    if (typeof window !== "undefined") {
      location.pathname = window.location.pathname;
      location.search = window.location.search;
    }
    const { data } = await getCustomerServiceViewUrl({
      variables: {
        headerFooterInput: {
          location
        }
      }
    });
    if (((_c = (_b = (_a = data == null ? void 0 : data.headerFooterContext) == null ? void 0 : _a.identityContext) == null ? void 0 : _b.act) == null ? void 0 : _c.csToggleHref) && typeof window !== "undefined") {
      window.location.href = data.headerFooterContext.identityContext.act.csToggleHref;
    }
  };
  return /* @__PURE__ */ jsx(Box, { background: "infoLight", paddingY: "medium", children: /* @__PURE__ */ jsx(PageBlock, { width: "full", children: /* @__PURE__ */ jsxs(Text, { size: "small", align: "center", children: [
    /* @__PURE__ */ jsx(Strong, { data: { "dd-privacy": "mask", "hj-masked": true }, children: actor }),
    " ",
    "is impersonating ",
    actAs,
    ".",
    " ",
    /* @__PURE__ */ jsx(TextLinkButton, { onClick: onCsToggleClick, children: isCsView ? "Switch to user view" : "Switch to CS view" })
  ] }) }) });
};
const getFilteredHeaderNavLinks = (rawNavItems, site) => {
  const filteredNavItems = rawNavItems.filter(
    (item) => filterLinksOnSiteAndHref(item, site)
  );
  return filteredNavItems.map((item) => {
    if (item.subItems && item.subItems.length > 0) {
      return {
        ...item,
        subItems: item.subItems.filter(
          (subItem) => filterLinksOnSiteAndHref(subItem, site)
        )
      };
    }
    return item;
  });
};
const useHeaderNavItems = (site, urlResolver, permissionOptions) => {
  const { t } = useTranslations(translations$1);
  const navigateToDefaultAnalytics = () => {
    let path;
    if (permissionOptions.canViewManagerReports) {
      path = pageUrl.URL_ANALYTICS_AD_USAGE;
    } else if (permissionOptions.canViewInsightsRoleReport) {
      path = pageUrl.URL_ANALYTICS_ROLE_INSIGHTS;
    } else {
      path = pageUrl.URL_ANALYTICS;
    }
    return urlResolver({ path });
  };
  const authenticatedNavItems = [
    {
      id: "home",
      label: t("Home"),
      href: urlResolver({
        path: pageUrl.URL_DASHBOARD
      }),
      "data-analytics": analyticsEvents.TRACK_HOME,
      "data-test": testTags.HOME,
      analyticsEvent: analyticsDesktopHeaderEvents.home
    },
    {
      id: "jobs",
      label: t("Jobs"),
      href: urlResolver({
        path: pageUrl.URL_LIST_JOBS_OPEN
      }),
      "data-analytics": analyticsEvents.TRACK_LIST_JOBS,
      "data-test": testTags.JOBS,
      analyticsEvent: analyticsDesktopHeaderEvents.jobs,
      rel: "nofollow",
      subItems: [
        {
          id: "open",
          label: t("Open"),
          href: urlResolver({
            path: pageUrl.URL_LIST_JOBS_OPEN
          }),
          "data-analytics": analyticsEvents.TRACK_LIST_JOBS_OPEN,
          "data-test": testTags.JOBS_OPEN,
          analyticsEvent: analyticsDesktopHeaderEvents.jobsOpen,
          rel: "nofollow"
        },
        {
          id: "expired",
          label: t("Expired"),
          href: urlResolver({
            path: pageUrl.URL_LIST_JOBS_EXPIRED
          }),
          "data-analytics": analyticsEvents.TRACK_LIST_JOBS_EXPIRED,
          "data-test": testTags.JOBS_EXPIRED,
          analyticsEvent: analyticsDesktopHeaderEvents.jobsExpired,
          rel: "nofollow"
        },
        {
          id: "draft",
          label: t("Draft"),
          href: urlResolver({
            path: pageUrl.URL_LIST_JOBS_DRAFT
          }),
          "data-analytics": analyticsEvents.TRACK_LIST_JOBS_DRAFT,
          "data-test": testTags.JOBS_DRAFT,
          analyticsEvent: analyticsDesktopHeaderEvents.jobsDraft,
          rel: "nofollow"
        },
        {
          id: "candidates",
          label: t("Past applicants"),
          href: urlResolver({
            path: pageUrl.URL_YOUR_CANDIDATES
          }),
          "data-analytics": analyticsEvents.TRACK_MANAGE_CANDIDATES,
          "data-test": testTags.JOBS_MANAGE_CANDIDATES,
          analyticsEvent: analyticsDesktopHeaderEvents.jobsYourCandidates,
          rel: "nofollow"
        }
      ]
    },
    {
      id: "products",
      label: t("Products"),
      href: urlResolver({
        path: pageUrl.URL_PRODUCTS_AD_BUDGETS
      }),
      "data-analytics": analyticsEvents.TRACK_PRODUCTS,
      "data-test": testTags.PRODUCTS,
      analyticsEvent: analyticsDesktopHeaderEvents.products,
      subItems: [
        {
          id: "whatsNew",
          label: t("What’s new"),
          href: urlResolver({
            path: pageUrl.URL_PRODUCTS_HUB
          }),
          "data-analytics": analyticsEvents.TRACK_PRODUCTS_HUB,
          "data-test": testTags.PRODUCTS_HUB,
          analyticsEvent: analyticsDesktopHeaderEvents.productsHubWhatsNew
        },
        {
          id: "jobAds",
          label: t("Job ads"),
          href: urlResolver({
            path: pageUrl.URL_PRODUCTS_JOB_ADS
          }),
          "data-analytics": analyticsEvents.TRACK_JOB_ADS_PRODUCT,
          "data-test": testTags.PRODUCTS_JOB_ADS,
          analyticsEvent: analyticsDesktopHeaderEvents.productsJobAds
        },
        {
          id: "discounts",
          label: t("Discounts"),
          href: urlResolver({
            path: pageUrl.URL_PRODUCTS_AD_BUDGETS
          }),
          "data-analytics": analyticsEvents.TRACK_DISCOUNTS_PRODUCT,
          "data-test": testTags.PRODUCTS_DISCOUNTS,
          analyticsEvent: analyticsDesktopHeaderEvents.productsDiscounts
        },
        {
          id: "findARecruiter",
          label: "Find a recruiter",
          href: urlResolver({
            path: pageUrl.URL_PRODUCTS_FIND_A_RECRUITER
          }),
          "data-analytics": analyticsEvents.TRACK_FIND_A_RECRUITER_PRODUCT,
          "data-test": testTags.PRODUCTS_DISCOUNTS,
          analyticsEvent: analyticsDesktopHeaderEvents.productFindARecruiter,
          specificSite: [siteNames.employer.seek.au]
        },
        {
          id: "candidateManagement",
          label: t("Candidate management"),
          href: urlResolver({
            path: pageUrl.URL_PRODUCTS_CANDIDATE_MANAGEMENT
          }),
          "data-analytics": analyticsEvents.TRACK_CANDIDATE_MANAGEMENT_PRODUCT,
          "data-test": testTags.PRODUCTS_CANDIDATE_MANAGEMENT,
          analyticsEvent: analyticsDesktopHeaderEvents.productsCandidateManagement
        },
        {
          id: "talentSearch",
          label: t("Talent search"),
          href: urlResolver({
            path: pageUrl.URL_PRODUCTS_TALENT_SEARCH
          }),
          "data-analytics": analyticsEvents.TRACK_TALENT_SEARCH_PRODUCT,
          "data-test": testTags.PRODUCTS_TALENT_SEARCH,
          analyticsEvent: analyticsDesktopHeaderEvents.productsTalentSearch
        },
        {
          id: "branding",
          label: t("Branding"),
          href: urlResolver({
            path: pageUrl.URL_PRODUCTS_BRANDING
          }),
          "data-analytics": analyticsEvents.TRACK_BRANDING_PRODUCT,
          "data-test": testTags.PRODUCTS_BRANDING,
          analyticsEvent: analyticsDesktopHeaderEvents.productsBranding
        },
        {
          id: "companyProfiles",
          label: t("Company profiles"),
          href: urlResolver({
            path: pageUrl.URL_PRODUCTS_COMPANY_PROFILES
          }),
          "data-analytics": analyticsEvents.TRACK_COMPANY_PROFILES_PRODUCT,
          "data-test": testTags.PRODUCTS_COMPANY_PROFILES,
          analyticsEvent: analyticsDesktopHeaderEvents.productsCompanyProfiles
        }
      ]
    },
    {
      id: "hiringAdvice",
      label: t("Hiring Advice"),
      href: urlResolver({
        path: pageUrl.URL_BLOG_HIRING_ADVICE_ROOT
      }),
      "data-analytics": analyticsEvents.TRACK_BLOG_HIRING_ADVICE,
      "data-test": testTags.HIRING_ADVICE,
      analyticsEvent: analyticsDesktopHeaderEvents.hiringAdvice,
      subItems: [
        {
          id: "attractingCandidates",
          label: t("Attracting candidates"),
          href: urlResolver({
            path: pageUrl.URL_BLOG_HIRING_ADVICE_CATEGORY_ATTRACTING_CANDIDATES
          }),
          "data-analytics": analyticsEvents.TRACK_BLOG_CATEGORY_ATTRACTING_CANDIDATES,
          "data-test": testTags.HIRING_ADVICE_ATTRACTING_CANDIDATES,
          analyticsEvent: analyticsDesktopHeaderEvents.hiringAdviceAttractingCandidates,
          specificSite: [
            siteNames.employer.seek.au,
            siteNames.employer.seek.hk,
            siteNames.employer.seek.id,
            siteNames.employer.seek.my,
            siteNames.employer.seek.nz,
            siteNames.employer.seek.ph,
            siteNames.employer.seek.sg,
            siteNames.employer.seek.th
          ]
        },
        {
          id: "hiringProcess",
          label: t("Hiring process"),
          href: urlResolver({
            path: pageUrl.URL_BLOG_HIRING_ADVICE_CATEGORY_HIRING_PROCESS
          }),
          "data-analytics": analyticsEvents.TRACK_BLOG_CATEGORY_HIRING_PROCESS,
          "data-test": testTags.HIRING_ADVICE_HIRING_PROCESS,
          analyticsEvent: analyticsDesktopHeaderEvents.hiringAdviceHiringProcess,
          specificSite: [
            siteNames.employer.seek.au,
            siteNames.employer.seek.hk,
            siteNames.employer.seek.id,
            siteNames.employer.seek.my,
            siteNames.employer.seek.nz,
            siteNames.employer.seek.ph,
            siteNames.employer.seek.sg,
            siteNames.employer.seek.th
          ]
        },
        {
          id: "engagingYourWorkforce",
          label: t("Engaging your workforce"),
          href: urlResolver({
            path: pageUrl.URL_BLOG_HIRING_ADVICE_CATEGORY_ENGAGING_WORKFORCE
          }),
          "data-analytics": analyticsEvents.TRACK_BLOG_CATEGORY_ENGAGING_WORKFORCE,
          "data-test": testTags.HIRING_ADVICE_ENGAGING_WORKFORCE,
          analyticsEvent: analyticsDesktopHeaderEvents.hiringAdviceEngagingYourWorkforce,
          specificSite: [
            siteNames.employer.seek.au,
            siteNames.employer.seek.hk,
            siteNames.employer.seek.id,
            siteNames.employer.seek.my,
            siteNames.employer.seek.nz,
            siteNames.employer.seek.ph,
            siteNames.employer.seek.sg,
            siteNames.employer.seek.th
          ]
        }
      ]
    },
    {
      id: "marketInsights",
      label: t("Market Insights"),
      href: urlResolver({
        path: pageUrl.URL_BLOG_MARKET_INSIGHTS_ROOT
      }),
      "data-analytics": analyticsEvents.TRACK_BLOG_MARKET_INSIGHTS,
      "data-test": testTags.MARKET_INSIGHTS,
      analyticsEvent: analyticsDesktopHeaderEvents.marketInsights,
      subItems: [
        {
          id: "employmentSalaryTrends",
          label: t("Employment and salary trends"),
          href: urlResolver({
            path: pageUrl.URL_BLOG_MARKET_INSIGHTS_CATEGORY_EMPLOYMENT_SALARY_TRENDS
          }),
          "data-analytics": analyticsEvents.TRACK_BLOG_EMPLOYMENT_SALARY_TRENDS,
          "data-test": testTags.MARKET_INSIGHTS_EMPLOYMENT_SALARY_TRENDS,
          analyticsEvent: analyticsDesktopHeaderEvents.marketInsightsEmploymentSalaryTrends
        },
        {
          id: "candidateInsights",
          label: t("Candidate insights"),
          href: urlResolver({
            path: pageUrl.URL_BLOG_MARKET_INSIGHTS_CATEGORY_CANDIDATE_INSIGHTS
          }),
          "data-analytics": analyticsEvents.TRACK_BLOG_CANDIDATE_INSIGHTS,
          "data-test": testTags.MARKET_INSIGHTS_CANDIDATE_INSIGHTS,
          analyticsEvent: analyticsDesktopHeaderEvents.marketInsightsCandidateInsights,
          specificSite: [
            siteNames.employer.seek.au,
            siteNames.employer.seek.id,
            siteNames.employer.seek.nz,
            siteNames.employer.seek.th
          ]
        },
        {
          id: "talentAttractionLab",
          label: t("Talent Attraction Lab"),
          href: urlResolver({
            path: pageUrl.URL_BLOG_MARKET_INSIGHTS_TALENT_ATTRACTION_LAB
          }),
          "data-analytics": analyticsEvents.TRACK_BLOG_TALENT_ATTRACTION_LAB,
          "data-test": testTags.MARKET_INSIGHTS_TALENT_ATTRACTION_LAB,
          analyticsEvent: analyticsDesktopHeaderEvents.marketInsightsTalentAttractionLab,
          specificSite: [
            siteNames.employer.seek.hk,
            siteNames.employer.seek.my,
            siteNames.employer.seek.ph,
            siteNames.employer.seek.sg
          ]
        },
        {
          id: "workforceInsights",
          label: t("Workforce insights"),
          href: urlResolver({
            path: pageUrl.URL_BLOG_MARKET_INSIGHTS_CATEGORY_WORKFORCE_INSIGHTS
          }),
          "data-analytics": analyticsEvents.TRACK_BLOG_WORKFORCE_INSIGHTS,
          "data-test": testTags.MARKET_INSIGHTS_WORKFORCE_INSIGHTS,
          analyticsEvent: analyticsDesktopHeaderEvents.marketInsightsWorkforceInsights,
          specificSite: [
            siteNames.employer.seek.au,
            siteNames.employer.seek.hk,
            siteNames.employer.seek.id,
            siteNames.employer.seek.my,
            siteNames.employer.seek.nz,
            siteNames.employer.seek.ph,
            siteNames.employer.seek.sg,
            siteNames.employer.seek.th
          ]
        },
        {
          id: "reports",
          label: t("Reports"),
          href: urlResolver({
            path: pageUrl.URL_BLOG_MARKET_INSIGHTS_CATEGORY_REPORTS
          }),
          "data-analytics": analyticsEvents.TRACK_BLOG_REPORTS,
          "data-test": testTags.MARKET_INSIGHTS_REPORTS,
          analyticsEvent: analyticsDesktopHeaderEvents.marketInsightsReports,
          specificSite: [
            siteNames.employer.seek.hk,
            siteNames.employer.seek.id,
            siteNames.employer.seek.my,
            siteNames.employer.seek.ph,
            siteNames.employer.seek.sg,
            siteNames.employer.seek.th
          ]
        },
        {
          id: "lawsOfAttraction",
          label: "Laws of Attraction",
          href: urlResolver({
            path: pageUrl.URL_LAWS_OF_ATTRACTION,
            product: "candidate"
          }),
          "data-analytics": analyticsEvents.TRACK_BLOG_LAWS_OF_ATTRACTION,
          "data-test": testTags.MARKET_INSIGHTS_LAWS_OF_ATTRACTION,
          analyticsEvent: analyticsDesktopHeaderEvents.marketInsightsLawsOfAttraction,
          specificSite: [
            siteNames.employer.seek.au,
            siteNames.employer.seek.nz
          ]
        }
      ]
    },
    {
      id: "analytics",
      label: t("Analytics"),
      href: navigateToDefaultAnalytics(),
      "data-analytics": analyticsEvents.TRACK_ANALYTICS,
      "data-test": testTags.ANALYTICS,
      analyticsEvent: analyticsDesktopHeaderEvents.analytics,
      subItems: [
        {
          id: "adUsage",
          label: t("Ad usage"),
          href: urlResolver({
            path: pageUrl.URL_ANALYTICS_AD_USAGE
          }),
          "data-analytics": analyticsEvents.TRACK_ANALYTICS_AD_USAGE,
          "data-test": testTags.ANALYTICS_AD_USAGE,
          analyticsEvent: analyticsDesktopHeaderEvents.analyticsAdUsageReport
        },
        {
          id: "talentSearchUsage",
          label: t("Talent Search usage"),
          href: urlResolver({
            path: pageUrl.URL_ANALYTICS_TALENT_SEARCH_USAGE
          }),
          "data-analytics": analyticsEvents.TRACK_ANALYTICS_TALENT_SEARCH_USAGE,
          "data-test": testTags.ANALYTICS_TALENT_SEARCH_USAGE,
          analyticsEvent: analyticsDesktopHeaderEvents.analyticsTalentSearchUsageReport
        },
        {
          id: "roleInsights",
          label: t("Role Insights"),
          href: urlResolver({
            path: pageUrl.URL_ANALYTICS_ROLE_INSIGHTS
          }),
          isBeta: true,
          "data-analytics": analyticsEvents.TRACK_ANALYTICS_ROLE_INSIGHTS,
          "data-test": testTags.ANALYTICS_ROLE_INSIGHTS,
          analyticsEvent: analyticsDesktopHeaderEvents.analyticsRoleInsights,
          specificSite: [
            siteNames.employer.seek.au,
            siteNames.employer.seek.nz
          ]
        }
      ]
    }
  ];
  const unauthenticatedNavItems = [
    {
      id: "home",
      label: t("Home"),
      href: urlResolver({
        path: pageUrl.URL_ROOT
      }),
      "data-analytics": analyticsEvents.TRACK_HOME,
      "data-test": testTags.HOME,
      analyticsEvent: analyticsDesktopHeaderEvents.home
    },
    {
      id: "products",
      label: t("Products"),
      href: urlResolver({
        path: pageUrl.URL_PRODUCTS_AD_BUDGETS
      }),
      "data-analytics": analyticsEvents.TRACK_PRODUCTS,
      "data-test": testTags.PRODUCTS,
      analyticsEvent: analyticsDesktopHeaderEvents.products,
      subItems: [
        {
          id: "whatsNew",
          label: t("What’s new"),
          href: urlResolver({
            path: pageUrl.URL_PRODUCTS_HUB
          }),
          "data-analytics": analyticsEvents.TRACK_PRODUCTS_HUB,
          "data-test": testTags.PRODUCTS_HUB,
          analyticsEvent: analyticsDesktopHeaderEvents.productsHubWhatsNew
        },
        {
          id: "jobAds",
          label: t("Job ads"),
          href: urlResolver({
            path: pageUrl.URL_PRODUCTS_JOB_ADS
          }),
          "data-analytics": analyticsEvents.TRACK_JOB_ADS_PRODUCT,
          "data-test": testTags.PRODUCTS_JOB_ADS,
          analyticsEvent: analyticsDesktopHeaderEvents.productsJobAds
        },
        {
          id: "discounts",
          label: t("Discounts"),
          href: urlResolver({
            path: pageUrl.URL_PRODUCTS_AD_BUDGETS
          }),
          "data-analytics": analyticsEvents.TRACK_DISCOUNTS_PRODUCT,
          "data-test": testTags.PRODUCTS_DISCOUNTS,
          analyticsEvent: analyticsDesktopHeaderEvents.productsDiscounts
        },
        {
          id: "findARecruiter",
          label: "Find a recruiter",
          href: urlResolver({
            path: pageUrl.URL_PRODUCTS_FIND_A_RECRUITER
          }),
          "data-analytics": analyticsEvents.TRACK_FIND_A_RECRUITER_PRODUCT,
          "data-test": testTags.PRODUCTS_DISCOUNTS,
          analyticsEvent: analyticsDesktopHeaderEvents.productFindARecruiter,
          specificSite: [siteNames.employer.seek.au]
        },
        {
          id: "candidateManagement",
          label: t("Candidate management"),
          href: urlResolver({
            path: pageUrl.URL_PRODUCTS_CANDIDATE_MANAGEMENT
          }),
          "data-analytics": analyticsEvents.TRACK_CANDIDATE_MANAGEMENT_PRODUCT,
          "data-test": testTags.PRODUCTS_CANDIDATE_MANAGEMENT,
          analyticsEvent: analyticsDesktopHeaderEvents.productsCandidateManagement
        },
        {
          id: "talentSearch",
          label: t("Talent search"),
          href: urlResolver({
            path: pageUrl.URL_PRODUCTS_TALENT_SEARCH
          }),
          "data-analytics": analyticsEvents.TRACK_TALENT_SEARCH_PRODUCT,
          "data-test": testTags.PRODUCTS_TALENT_SEARCH,
          analyticsEvent: analyticsDesktopHeaderEvents.productsTalentSearch
        },
        {
          id: "branding",
          label: t("Branding"),
          href: urlResolver({
            path: pageUrl.URL_PRODUCTS_BRANDING
          }),
          "data-analytics": analyticsEvents.TRACK_BRANDING_PRODUCT,
          "data-test": testTags.PRODUCTS_BRANDING,
          analyticsEvent: analyticsDesktopHeaderEvents.productsBranding
        },
        {
          id: "companyProfiles",
          label: t("Company profiles"),
          href: urlResolver({
            path: pageUrl.URL_PRODUCTS_COMPANY_PROFILES
          }),
          "data-analytics": analyticsEvents.TRACK_COMPANY_PROFILES_PRODUCT,
          "data-test": testTags.PRODUCTS_COMPANY_PROFILES,
          analyticsEvent: analyticsDesktopHeaderEvents.productsCompanyProfiles
        }
      ]
    },
    {
      id: "hiringAdvice",
      label: t("Hiring Advice"),
      href: urlResolver({
        path: pageUrl.URL_BLOG_HIRING_ADVICE_ROOT
      }),
      "data-analytics": analyticsEvents.TRACK_BLOG_HIRING_ADVICE,
      "data-test": testTags.HIRING_ADVICE,
      analyticsEvent: analyticsDesktopHeaderEvents.hiringAdvice,
      subItems: [
        {
          id: "attractingCandidates",
          label: t("Attracting candidates"),
          href: urlResolver({
            path: pageUrl.URL_BLOG_HIRING_ADVICE_CATEGORY_ATTRACTING_CANDIDATES
          }),
          "data-analytics": analyticsEvents.TRACK_BLOG_CATEGORY_ATTRACTING_CANDIDATES,
          "data-test": testTags.HIRING_ADVICE_ATTRACTING_CANDIDATES,
          analyticsEvent: analyticsDesktopHeaderEvents.hiringAdviceAttractingCandidates,
          specificSite: [
            siteNames.employer.seek.au,
            siteNames.employer.seek.hk,
            siteNames.employer.seek.id,
            siteNames.employer.seek.my,
            siteNames.employer.seek.nz,
            siteNames.employer.seek.ph,
            siteNames.employer.seek.sg,
            siteNames.employer.seek.th
          ]
        },
        {
          id: "hiringProcess",
          label: t("Hiring process"),
          href: urlResolver({
            path: pageUrl.URL_BLOG_HIRING_ADVICE_CATEGORY_HIRING_PROCESS
          }),
          "data-analytics": analyticsEvents.TRACK_BLOG_CATEGORY_HIRING_PROCESS,
          "data-test": testTags.HIRING_ADVICE_HIRING_PROCESS,
          analyticsEvent: analyticsDesktopHeaderEvents.hiringAdviceHiringProcess,
          specificSite: [
            siteNames.employer.seek.au,
            siteNames.employer.seek.hk,
            siteNames.employer.seek.id,
            siteNames.employer.seek.my,
            siteNames.employer.seek.nz,
            siteNames.employer.seek.ph,
            siteNames.employer.seek.sg,
            siteNames.employer.seek.th
          ]
        },
        {
          id: "engagingYourWorkforce",
          label: t("Engaging your workforce"),
          href: urlResolver({
            path: pageUrl.URL_BLOG_HIRING_ADVICE_CATEGORY_ENGAGING_WORKFORCE
          }),
          "data-analytics": analyticsEvents.TRACK_BLOG_CATEGORY_ENGAGING_WORKFORCE,
          "data-test": testTags.HIRING_ADVICE_ENGAGING_WORKFORCE,
          analyticsEvent: analyticsDesktopHeaderEvents.hiringAdviceEngagingYourWorkforce,
          specificSite: [
            siteNames.employer.seek.au,
            siteNames.employer.seek.hk,
            siteNames.employer.seek.id,
            siteNames.employer.seek.my,
            siteNames.employer.seek.nz,
            siteNames.employer.seek.ph,
            siteNames.employer.seek.sg,
            siteNames.employer.seek.th
          ]
        }
      ]
    },
    {
      id: "marketInsights",
      label: t("Market Insights"),
      href: urlResolver({
        path: pageUrl.URL_BLOG_MARKET_INSIGHTS_ROOT
      }),
      "data-analytics": analyticsEvents.TRACK_BLOG_MARKET_INSIGHTS,
      "data-test": testTags.MARKET_INSIGHTS,
      analyticsEvent: analyticsDesktopHeaderEvents.marketInsights,
      subItems: [
        {
          id: "employmentSalaryTrends",
          label: t("Employment and salary trends"),
          href: urlResolver({
            path: pageUrl.URL_BLOG_MARKET_INSIGHTS_CATEGORY_EMPLOYMENT_SALARY_TRENDS
          }),
          "data-analytics": analyticsEvents.TRACK_BLOG_EMPLOYMENT_SALARY_TRENDS,
          analyticsEvent: analyticsDesktopHeaderEvents.marketInsightsEmploymentSalaryTrends
        },
        {
          id: "candidateInsights",
          label: t("Candidate insights"),
          href: urlResolver({
            path: pageUrl.URL_BLOG_MARKET_INSIGHTS_CATEGORY_CANDIDATE_INSIGHTS
          }),
          "data-analytics": analyticsEvents.TRACK_BLOG_CANDIDATE_INSIGHTS,
          "data-test": testTags.MARKET_INSIGHTS_CANDIDATE_INSIGHTS,
          analyticsEvent: analyticsDesktopHeaderEvents.marketInsightsCandidateInsights,
          specificSite: [
            siteNames.employer.seek.au,
            siteNames.employer.seek.id,
            siteNames.employer.seek.nz,
            siteNames.employer.seek.th
          ]
        },
        {
          id: "talentAttractionLab",
          label: t("Talent Attraction Lab"),
          href: urlResolver({
            path: pageUrl.URL_BLOG_MARKET_INSIGHTS_TALENT_ATTRACTION_LAB
          }),
          "data-analytics": analyticsEvents.TRACK_BLOG_TALENT_ATTRACTION_LAB,
          "data-test": testTags.MARKET_INSIGHTS_TALENT_ATTRACTION_LAB,
          analyticsEvent: analyticsDesktopHeaderEvents.marketInsightsTalentAttractionLab,
          specificSite: [
            siteNames.employer.seek.hk,
            siteNames.employer.seek.my,
            siteNames.employer.seek.ph,
            siteNames.employer.seek.sg
          ]
        },
        {
          id: "workforceInsights",
          label: t("Workforce insights"),
          href: urlResolver({
            path: pageUrl.URL_BLOG_MARKET_INSIGHTS_CATEGORY_WORKFORCE_INSIGHTS
          }),
          "data-analytics": analyticsEvents.TRACK_BLOG_WORKFORCE_INSIGHTS,
          "data-test": testTags.MARKET_INSIGHTS_WORKFORCE_INSIGHTS,
          analyticsEvent: analyticsDesktopHeaderEvents.marketInsightsWorkforceInsights,
          specificSite: [
            siteNames.employer.seek.au,
            siteNames.employer.seek.hk,
            siteNames.employer.seek.id,
            siteNames.employer.seek.my,
            siteNames.employer.seek.nz,
            siteNames.employer.seek.ph,
            siteNames.employer.seek.sg,
            siteNames.employer.seek.th
          ]
        },
        {
          id: "reports",
          label: t("Reports"),
          href: urlResolver({
            path: pageUrl.URL_BLOG_MARKET_INSIGHTS_CATEGORY_REPORTS
          }),
          "data-analytics": analyticsEvents.TRACK_BLOG_REPORTS,
          "data-test": testTags.MARKET_INSIGHTS_REPORTS,
          analyticsEvent: analyticsDesktopHeaderEvents.marketInsightsReports,
          specificSite: [
            siteNames.employer.seek.hk,
            siteNames.employer.seek.id,
            siteNames.employer.seek.my,
            siteNames.employer.seek.ph,
            siteNames.employer.seek.sg,
            siteNames.employer.seek.th
          ]
        },
        {
          id: "lawsOfAttraction",
          label: "Laws of Attraction",
          href: urlResolver({
            path: pageUrl.URL_LAWS_OF_ATTRACTION,
            product: "candidate"
          }),
          "data-analytics": analyticsEvents.TRACK_BLOG_LAWS_OF_ATTRACTION,
          "data-test": testTags.MARKET_INSIGHTS_LAWS_OF_ATTRACTION,
          analyticsEvent: analyticsDesktopHeaderEvents.marketInsightsLawsOfAttraction,
          specificSite: [
            siteNames.employer.seek.au,
            siteNames.employer.seek.nz
          ]
        }
      ]
    },
    {
      id: "analytics",
      label: t("Analytics"),
      href: navigateToDefaultAnalytics(),
      "data-analytics": analyticsEvents.TRACK_ANALYTICS,
      "data-test": testTags.ANALYTICS,
      analyticsEvent: analyticsDesktopHeaderEvents.analytics
    }
  ];
  return {
    unauthenticatedNavItems: getFilteredHeaderNavLinks(
      unauthenticatedNavItems,
      site
    ),
    authenticatedNavItems: getFilteredHeaderNavLinks(
      authenticatedNavItems,
      site
    )
  };
};
const SITE_SKIP_TO_CONTENT = "start-of-content";
const EmployerHeader = ({
  identityContext: identity,
  user,
  advertiser,
  activeTabId,
  activeSubTabId,
  basic = false,
  divider: divider$1 = false,
  error,
  linkRenderer: LinkRenderer = DefaultLinkRenderer,
  onToggleButtonClick = () => {
  },
  onTrackedItemInteraction = () => {
  },
  id,
  locale,
  site,
  isStaging,
  localisedContactInfo
}) => {
  const { t } = useTranslations(translations$1);
  const urlResolver = createUrlResolver({
    site,
    language: locale,
    staging: isStaging
  });
  const isAgency = Boolean(advertiser == null ? void 0 : advertiser.isAgency);
  const permissionOptions = (advertiser == null ? void 0 : advertiser.permissionOptions) ?? defaultPermissionOptions;
  const showOnCreditHoldMessage = Boolean(advertiser == null ? void 0 : advertiser.showOnCreditHoldMessage);
  const shouldDisplayDesktopNavigation = !basic && !isAgency;
  const shouldDisplayDesktopSubNavigation = !basic && !isAgency;
  const authenticationStatus = getAuthenticationStatus(identity);
  const { authenticatedNavItems, unauthenticatedNavItems } = useHeaderNavItems(
    site,
    urlResolver,
    permissionOptions
  );
  const identityIsAuthenticated = isAuthenticated(authenticationStatus);
  const allNavItems = identityIsAuthenticated ? authenticatedNavItems : unauthenticatedNavItems;
  const navItems = allNavItems.filter(
    (item) => permissionOptions.canViewManagerReports || permissionOptions.canViewInsightsRoleReport || activeTabId === "analytics" || // This is to make sure that the analytics tab is always displayed when the user views Analytics products
    item.id !== "analytics"
  );
  const activeItem = navItems.find((item) => item.id === activeTabId);
  const subItems = activeItem && activeItem.subItems ? activeItem.subItems : [];
  const analyticsSubItemIds = permissionOptions.canViewManagerReports ? ["adUsage", "talentSearchUsage"] : [];
  if (permissionOptions.canViewInsightsRoleReport) {
    analyticsSubItemIds.push("roleInsights");
  }
  const activeSubItems = (activeItem == null ? void 0 : activeItem.id) === "analytics" ? subItems.filter((item) => analyticsSubItemIds.includes(item.id)) : subItems;
  const mobileNavItems = [...navItems].map(
    (item) => item.id === "analytics" && (activeItem == null ? void 0 : activeItem.id) === "analytics" ? {
      ...item,
      subItems: activeSubItems
    } : item
  );
  const handleItemInteraction = (event) => {
    onTrackedItemInteraction({
      ...event,
      accountPermissions: permissionOptions,
      authenticationStatus,
      isAgency,
      isSwitchAccountEnabled: Boolean(advertiser == null ? void 0 : advertiser.switchAccountsHref),
      isCreditOnHold: showOnCreditHoldMessage
    });
  };
  return /* @__PURE__ */ jsx(
    EmployerHeaderProvider,
    {
      urlResolver,
      site,
      locale,
      identityContext: identity,
      localisedContactInfo,
      user,
      advertiser,
      basic,
      error,
      handleInteractionTracking: handleItemInteraction,
      children: /* @__PURE__ */ jsxs(
        ChatProvider,
        {
          authenticationStatus,
          isAgency,
          children: [
            (identity == null ? void 0 : identity.act) && /* @__PURE__ */ jsx(ImpersonationBanner, {}),
            /* @__PURE__ */ jsxs(
              "header",
              {
                role: "banner",
                "aria-label": t("Site Header"),
                style: { position: "relative" },
                children: [
                  /* @__PURE__ */ jsx(ScreenReaderSkipLink, { to: SITE_SKIP_TO_CONTENT, children: t("Skip to content") }),
                  /* @__PURE__ */ jsx(
                    Box,
                    {
                      position: "relative",
                      className: [
                        root,
                        {
                          [divider]: divider$1
                        }
                      ],
                      background: "surface",
                      children: /* @__PURE__ */ jsx(PageBlock, { width: "full", children: /* @__PURE__ */ jsxs(
                        Box,
                        {
                          display: "flex",
                          alignItems: "center",
                          width: "full",
                          flexWrap: "wrap",
                          children: [
                            /* @__PURE__ */ jsx(
                              Box,
                              {
                                display: "flex",
                                flexWrap: "wrap",
                                className: logoContent,
                                paddingRight: "medium",
                                children: /* @__PURE__ */ jsx(BrandedLogo, { linkRenderer: LinkRenderer })
                              }
                            ),
                            /* @__PURE__ */ jsxs(
                              Box,
                              {
                                display: "flex",
                                flexWrap: "wrap",
                                "flex-direction": "row-reverse",
                                flexGrow: 1,
                                children: [
                                  shouldDisplayDesktopNavigation && /* @__PURE__ */ jsx(
                                    DesktopNavigation,
                                    {
                                      items: navItems,
                                      activeTabId,
                                      linkRenderer: LinkRenderer
                                    }
                                  ),
                                  /* @__PURE__ */ jsx(
                                    Box,
                                    {
                                      display: "flex",
                                      justifyContent: "flexEnd",
                                      className: actionsContent,
                                      children: /* @__PURE__ */ jsx(
                                        HeaderActions,
                                        {
                                          id,
                                          onToggleButtonClick,
                                          linkRenderer: LinkRenderer,
                                          site,
                                          language: getLanguageFromLocale(locale),
                                          identityIsAuthenticated
                                        }
                                      )
                                    }
                                  )
                                ]
                              }
                            ),
                            /* @__PURE__ */ jsx(
                              MobileMenuDropdown,
                              {
                                id,
                                activeTabId,
                                activeSubTabId,
                                items: mobileNavItems,
                                onToggleButtonClick,
                                linkRenderer: LinkRenderer
                              }
                            )
                          ]
                        }
                      ) })
                    }
                  ),
                  shouldDisplayDesktopSubNavigation && /* @__PURE__ */ jsx(
                    DesktopSubNavigation,
                    {
                      activeSubTabId,
                      subItems: activeSubItems,
                      linkRenderer: LinkRenderer
                    }
                  )
                ]
              }
            ),
            showOnCreditHoldMessage && /* @__PURE__ */ jsx(CreditHoldAlert, { site }),
            /* @__PURE__ */ jsx(ScreenReaderSkipTarget, { name: SITE_SKIP_TO_CONTENT })
          ]
        }
      )
    }
  );
};
export {
  EmployerHeader,
  testTags,
  useChatContext
};
